import type { PlanName } from '~/composables/usePlanConfig'

export interface PlanCardDef {
  key: PlanName
  title: string
  description: string
  features: string[]
}

/**
 * Shared plan card copy for /pricing and /dashboard/settings/billing.
 * Qualitative feature overview only, no counts or limits.
 *
 * Conventions relied on elsewhere:
 * - Index 0 of pro/elite is the "Everything in X, plus:" header (bolded by PlanCards.vue).
 * - Pro's last bullet is the exact string '7-day free trial' (swapped by planCardsForMode in onetime mode).
 */
export const PLAN_CARDS: PlanCardDef[] = [
  {
    key: 'free',
    title: 'Free',
    description: 'Build your page and start sharing it with the world.',
    features: [
      'Unique mychar.gg/username URL',
      'Unlimited platform links',
      'Avatar, background & music player',
      'Discord integration',
      'Game library, achievements & setup section',
      'Friends & section reordering',
      'Basic analytics'
    ]
  },
  {
    key: 'pro',
    title: 'Pro',
    description: 'Unlock full creative control and make your page truly stand out.',
    features: [
      'Everything in Free, plus:',
      'Background effects, gradient & cursor trails',
      'Video backgrounds & video clips',
      'Custom links with your own label & icon',
      'YouTube & Twitch integrations',
      'All fonts, layouts, link & icon styles',
      'Display name & rich text bio',
      'Custom section titles & enter label',
      'Comment section',
      'Advanced analytics & live visitors',
      'No mychar.gg branding',
      'Higher upload limits',
      '7-day free trial'
    ]
  },
  {
    key: 'elite',
    title: 'Elite',
    description: 'Exclusive effects, custom fonts, and the highest limits.',
    features: [
      'Everything in Pro, plus:',
      'Exclusive effects & cursor trails',
      'Exclusive icon styles & grid layout',
      'Custom font uploads',
      'Unlimited custom links & friends',
      'Show links as embeds',
      'Lifetime analytics',
      'The highest upload limits',
      'Priority support'
    ]
  }
]

/** PLAN_CARDS with Pro's trial bullet swapped for the lifetime note in one-time payment mode. */
export function planCardsForMode(isOnetime: boolean): PlanCardDef[] {
  return PLAN_CARDS.map(p => ({
    ...p,
    features: p.features.map(f => f === '7-day free trial' && isOnetime ? 'Pay once · Lifetime access' : f)
  }))
}
